import { ForwardedRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMatomo } from '@jonkoops/matomo-tracker-react'
import { Box, Typography, useTheme } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'

import { testHandle, useUserContext } from '@acre/utils'
import { useGetCasesQuery } from '@acre/graphql'
import { trackEventFiltered } from '@acre/design-system'

import { GlobalSearchOption } from '..'
import { formatCaseOption } from './GlobalSearchRecentCases.helpers'
import Header from './Header'
import { Container } from './GlobalSearchRecentCases.styles'

type Props = {
  display: boolean
  closeModalSearch: () => void
  forwardedRef?: ForwardedRef<HTMLDivElement>
}

const GlobalSearchRecentCases = ({ display, closeModalSearch, forwardedRef }: Props) => {
  const theme = useTheme()
  const { trackEvent } = useMatomo()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useUserContext()

  const [shouldRender, setShouldRender] = useState<boolean>(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const itemRef = useRef<(HTMLDivElement | null)[]>([])

  // Delay rendering of the component to avoid overlap with the search dropdown which has a delay when disappearing
  useEffect(() => {
    if (display) {
      const timer = setTimeout(() => {
        setShouldRender(true)
      }, 250)
      // Cleanup the timer on unmount
      return () => clearTimeout(timer)
    } else {
      // Reset the shouldRender state when display is false
      setShouldRender(false)
    }
  }, [display])

  // Reset container focus when the component is displayed
  useEffect(() => {
    if (shouldRender && containerRef.current) {
      containerRef.current.focus()
    }
  }, [shouldRender])

  // Scroll to the focused item when the focusedIndex changes
  useEffect(() => {
    if (focusedIndex >= 0 && itemRef.current[focusedIndex]) {
      itemRef.current[focusedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex])

  const recentCases = useMemo(() => {
    return user?.id ? JSON.parse(localStorage.getItem(`${user?.id}-recentlyViewedCases`) || '[]') : []
  }, [user?.id])

  const recentCaseIds = useMemo(
    () => recentCases.map((caseItem: { id: string; postcode: string }) => caseItem.id),
    [recentCases],
  )

  const { data, loading } = useGetCasesQuery({
    variables: {
      ids: recentCaseIds,
    },
    skip: !recentCaseIds.length,
  })

  const trackCaseView = useCallback(
    (caseId: string) => {
      trackEventFiltered(trackEvent, location, 'searchAction', 'viewRecentlyViewedCase', `viewCase-${caseId}`)
    },
    [trackEvent, location],
  )

  const formattedCases = useMemo(() => {
    return (data?.cases || []).map((caseInput) => {
      const postcode = recentCases.find(
        (caseItem: { id: string; postcode: string }) => caseItem.id === caseInput.id,
      )?.postcode
      return formatCaseOption(caseInput, postcode, closeModalSearch, trackCaseView, navigate)
    })
  }, [data?.cases, recentCases, closeModalSearch, trackCaseView, navigate])

  if (!display || !recentCaseIds.length || !shouldRender) return

  return (
    <Container
      ref={forwardedRef || containerRef}
      tabIndex={0}
      data-testid={testHandle('GlobalSearchRecentCases')}
      onKeyDown={(e) => {
        if (!formattedCases.length) return

        // Set focus on the case below when arrow down is pressed
        if (e.key === 'ArrowDown') {
          setFocusedIndex((prev) => (prev + 1) % formattedCases.length)
          e.preventDefault()
        }

        // Set focus on the case above when arrow up is pressed
        if (e.key === 'ArrowUp') {
          setFocusedIndex((prev) => (prev - 1 + formattedCases.length) % formattedCases.length)
          e.preventDefault()
        }

        // Navigate to focused case on Enter key press
        if (e.key === 'Enter' && focusedIndex >= 0) {
          const focusedCase = formattedCases[focusedIndex]
          if (focusedCase && focusedCase.id) {
            closeModalSearch()
            trackCaseView(focusedCase.id)
            navigate(`/cases/${focusedCase.id}/overview`, { replace: true })
          }
        }
      }}
    >
      <Header />
      {loading ? (
        <Box display="flex" justifyContent="center" p={theme.spacing(2)}>
          <Typography color={theme.colours.new.grey2} fontSize={theme.typography.pxToRem(14)}>
            <FormattedMessage id="cases.loading" />
          </Typography>
        </Box>
      ) : (
        <>
          {formattedCases.map((caseProps, index) => {
            return (
              <div key={caseProps.id} ref={(el) => (itemRef.current[index] = el)}>
                {GlobalSearchOption({ id: caseProps.id }, { ...caseProps, focused: index === focusedIndex })}
              </div>
            )
          })}
        </>
      )}
    </Container>
  )
}

export default GlobalSearchRecentCases
